// api.ts — cliente HTTP que fala com o Worker.
// Em dev usa VITE_API_URL=http://localhost:8787; em prod, workers.dev evita WAF challenge em fetch.

const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (import.meta.env.PROD
    ? "https://udischool-api.edavalon756.workers.dev"
    : "http://localhost:8787");

const JWT_KEY = "udi_admin_jwt";
const LEAD_TOKEN_KEY = "udi_lead_token";

export class ApiError extends Error {
  public status: number;
  public payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

type FetchOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: "admin" | "lead" | "none";
  signal?: AbortSignal;
};

export async function apiFetch<T = unknown>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, auth = "none", signal } = opts;
  const headers: Record<string, string> = { Accept: "application/json" };

  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (auth === "admin") {
    const token = localStorage.getItem(JWT_KEY);
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } else if (auth === "lead") {
    const token = sessionStorage.getItem(LEAD_TOKEN_KEY);
    if (token) headers["X-Lead-Token"] = token;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      credentials: "omit",
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    throw new ApiError(0, "network_error");
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* sem body */
  }

  if (!res.ok) {
    if (res.status === 401) {
      if (auth === "admin") {
        localStorage.removeItem(JWT_KEY);
        localStorage.removeItem("udi_admin_user");
      } else if (auth === "lead") {
        sessionStorage.removeItem(LEAD_TOKEN_KEY);
      }
    }

    const msg =
      (json && typeof json === "object" && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : null) ?? `http_${res.status}`;
    throw new ApiError(res.status, msg, json);
  }

  return json as T;
}

export const api = {
  base: API_BASE,
  jwtKey: JWT_KEY,
  leadTokenKey: LEAD_TOKEN_KEY,
};
